import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { ExportButton } from '@/components/common/ExportButton';
import { SkeletonLoader } from '@/components/common/StateViews';
import { fetchCadePautas } from '@/services/cade';
import type { CadePautaItem, CadePautaSession, CadeRecentPautas } from '@/services/types';

/** How many sessions the panel asks for. The newest opens; the rest are one click away. */
const SESSIONS = 4;

/**
 * The newest pautas the archive holds, by the date each one prints.
 *
 * Not a live calendar, and the panel says so in its own words: the archive is
 * loaded by hand, so the first line states the newest session's date and its age,
 * and a warning appears when it is old enough that a newer pauta likely exists.
 * An item says where the ARCHIVE stands on it -- decided in this session, decided
 * in another, not in the archive -- and never what the Tribunal will do with it.
 */
export function PautasPanel() {
  const { t } = useTranslation(['cade', 'common']);
  const { data, isPending, error } = useQuery({
    queryKey: ['cade', 'pautas', SESSIONS],
    queryFn: () => fetchCadePautas(SESSIONS),
  });

  if (isPending) return <SkeletonLoader rows={3} />;
  if (error) {
    return (
      <p role="alert" className="rounded-md border p-3 text-xs text-muted-foreground">
        {error.message}
      </p>
    );
  }

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4" data-pautas>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-foreground">{t('cade:pautas.title')}</h2>
        <ExportButton path={`/api/v1/cade/export/pautas?limit=${SESSIONS}`} />
      </div>
      <Answer answer={data.pautas} help={t('cade:pautas.help')}>
        {(pautas: CadeRecentPautas) => <Sessions pautas={pautas} />}
      </Answer>
    </section>
  );
}

function Sessions({ pautas }: { pautas: CadeRecentPautas }) {
  const { t, i18n } = useTranslation(['cade', 'common']);
  const day = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long', timeZone: 'UTC' });
  const latest = pautas.sessions[0];

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground" data-pauta-latest>
        {t('cade:pautas.latest', {
          session: sessionLabel(latest, t),
          date: day.format(new Date(`${latest.date}T00:00:00Z`)),
          count: pautas.age_days,
        })}
      </p>
      {pautas.stale ? (
        <p
          role="status"
          className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-2 text-xs text-foreground"
          data-pautas-stale
        >
          <AlertTriangle
            className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400"
            aria-hidden
          />
          {t('cade:pautas.stale')}
        </p>
      ) : null}
      {pautas.sessions.map((session, index) => (
        <details
          key={`${session.date}-${session.number ?? 'x'}`}
          open={index === 0}
          className="rounded-md border"
          data-pauta-session
        >
          <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-foreground">
            {sessionLabel(session, t)}
            <span className="ml-2 font-normal text-muted-foreground">
              {day.format(new Date(`${session.date}T00:00:00Z`))}
              {session.time ? ` · ${session.time}` : ''} ·{' '}
              {t('cade:pautas.items', { count: session.items.length })}
            </span>
          </summary>
          <Items session={session} />
        </details>
      ))}
      <p className="text-xs text-muted-foreground">
        {t('cade:pautas.footnote', {
          dated: pautas.dated_sessions,
          total: pautas.total_sessions,
        })}
      </p>
    </div>
  );
}

function sessionLabel(
  session: CadePautaSession,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const kind =
    session.kind === 'EXTRAORDINARY' ? t('cade:pautas.extraordinary') : t('cade:pautas.ordinary');
  return session.number !== null
    ? t('cade:pautas.session', { number: session.number, kind })
    : t('cade:pautas.sessionNoNumber', { kind });
}

function Items({ session }: { session: CadePautaSession }) {
  const { t } = useTranslation(['cade']);
  if (session.items.length === 0) {
    return <p className="px-3 pb-3 text-xs text-muted-foreground">{t('cade:pautas.noItems')}</p>;
  }
  return (
    <div className="overflow-x-auto px-3 pb-3">
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-muted-foreground">
          <tr>
            <th className="py-1 pr-3 font-medium">{t('cade:pautas.columns.case')}</th>
            <th className="py-1 pr-3 font-medium">{t('cade:pautas.columns.type')}</th>
            <th className="py-1 pr-3 font-medium">{t('cade:pautas.columns.relator')}</th>
            <th className="py-1 font-medium">{t('cade:pautas.columns.standing')}</th>
          </tr>
        </thead>
        <tbody>
          {session.items.map((item, index) => (
            <ItemRow key={`${item.case_number ?? 'x'}-${index}`} item={item} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ItemRow({ item }: { item: CadePautaItem }) {
  const { t } = useTranslation(['cade']);
  return (
    <tr className="border-t align-top" data-standing={item.standing}>
      <td className="whitespace-nowrap py-1.5 pr-3 tabular-nums">
        {item.case_key ? (
          <Link
            to={`/cade/c/${item.case_key}`}
            className="text-primary underline-offset-4 hover:underline"
          >
            {item.case_number}
          </Link>
        ) : (
          <span className="text-foreground">{item.case_number}</span>
        )}
        {item.case_number ? (
          <Link
            to={`/cade/dossier/${item.case_number.replace(/\D/g, '')}`}
            className="ml-2 text-xs text-primary underline underline-offset-4"
          >
            {t('cade:dossier.link')}
          </Link>
        ) : null}
      </td>
      <td className="py-1.5 pr-3 text-muted-foreground">
        {item.case_type
          ? t(`cade:pautas.caseTypes.${item.case_type}`, { defaultValue: item.case_type })
          : ''}
      </td>
      <td className="py-1.5 pr-3 text-muted-foreground">{item.relator ?? ''}</td>
      <td className="py-1.5 text-muted-foreground">{t(`cade:pautas.standing.${item.standing}`)}</td>
    </tr>
  );
}
